import { memo, useCallback, type ComponentPropsWithoutRef, type ReactElement } from "react";
import { useIntl, defineMessages } from "react-intl";

import { userFullName } from "util/user";
import { useMutation } from "util/graphql";
import { Portal, useId, b } from "util/html";
import Modal from "common/modal";
import { useForm } from "common/core/form";
import { TextInput } from "common/core/form/text";
import { FormRow } from "common/core/form/layout";
import { CheckboxLabel, Checkbox } from "common/core/form/option";
import {
  isAriaInvalid,
  FormattedFieldError,
  FieldErrorMessage,
  defaultRequiredMessage,
} from "common/core/form/error";
import MeetingQuery, {
  type NotaryMeeting_meeting_Meeting as RootNotaryMeeting,
  type NotaryMeeting,
  type NotaryMeetingVariables,
} from "common/meeting/notary/meeting_query.graphql";

import { usePromiseSubmitLock, Form, Fields, CheckboxContainer, Actions } from "./form";
import UpdateWitnessMutation from "./update_witness_mutation.graphql";
import Styles from "./attest.module.scss";

export type AttestFormValues = {
  attested: "yes";
  relationship: string;
};
type Props = {
  onSubmit: (formValues: AttestFormValues) => Promise<unknown>;
  onBack?: ComponentPropsWithoutRef<typeof Actions>["onBack"];
  witnessInformation: Parameters<typeof userFullName>[0];
  notaryUser: Parameters<typeof userFullName>[0];
};
type WithModalProps = {
  onClose: () => void;
  meetingId: string;
  witnessInformation: Props["witnessInformation"] & { userId: string | null };
  notaryUser: Props["notaryUser"];
};

const MESSAGES = defineMessages({
  witnessName: {
    id: "fef89f2b-1a7f-45d2-992d-e7b1709c0c63",
    defaultMessage: "Witness: {name}",
  },
  remoteAttestTitle: {
    id: "14d7ad4e-55e5-4368-bcd5-9d3824ed001a",
    defaultMessage: "Notary Identification of Remote Witness",
  },
  relationshipLabel: {
    id: "1ab6f322-697f-4fde-a1d6-c99b99f5c01c",
    defaultMessage: "How do you personally know the witness?",
  },
  saveButton: {
    id: "e9b9435c-e282-421d-82e2-4a3615aad85e",
    defaultMessage: "Add Witness",
  },
  attest: {
    id: "9fbcc6d0-dc7b-4dc5-a648-efd6369be7db",
    defaultMessage: "I, <b>{name}</b>, attest that I personally know the witness.",
  },
  requiredAttest: {
    id: "9837354d-e056-4830-bc73-023ba81e5b66",
    defaultMessage: "You must attest you know the witness before proceeding.",
  },
});

function useConfirmWitnessConfig(
  witnessInfo: WithModalProps["witnessInformation"],
  meetingId: string,
  onSuccess: () => void,
) {
  const { userId } = witnessInfo;
  const updateWitnessMutateFn = useMutation(UpdateWitnessMutation);
  return useCallback(
    ({ relationship }: AttestFormValues) => {
      if (!userId) {
        throw new Error("UserId must be defined");
      }
      const input = {
        userId,
        relationship,
      };
      return updateWitnessMutateFn({
        variables: { input },
        update(cacheProxy) {
          // This mutation does not return a witness participant object so we have to manually
          // update the cache.
          const data = cacheProxy.readQuery<NotaryMeeting, NotaryMeetingVariables>({
            query: MeetingQuery,
            variables: { meetingId },
          });
          const meeting = data!.meeting as RootNotaryMeeting;
          const newMeeting = {
            ...meeting,
            meetingParticipants: meeting.meetingParticipants.map((part) => {
              return part.__typename === "WitnessParticipant" && part.userId === userId
                ? {
                    ...part,
                    relationshipToParent: relationship,
                  }
                : part;
            }),
          };
          cacheProxy.writeQuery<NotaryMeeting, NotaryMeetingVariables>({
            data: { ...data!, meeting: newMeeting },
            query: MeetingQuery,
            variables: { meetingId },
          });
        },
      }).then(onSuccess);
    },
    [updateWitnessMutateFn, userId, meetingId],
  );
}

function AttestWitness({ onBack, onSubmit, witnessInformation, notaryUser }: Props) {
  const intl = useIntl();
  const form = useForm<AttestFormValues>();
  const [loading, handleSubmit] = usePromiseSubmitLock(onSubmit);
  const relationshipId = useId();
  const { errors } = form.formState;
  return (
    <Form onSubmit={form.handleSubmit(handleSubmit)}>
      <Fields>
        <h5 className={Styles.header}>
          {intl.formatMessage(MESSAGES.witnessName, { name: userFullName(witnessInformation) })}
        </h5>

        <CheckboxContainer>
          <div>
            <CheckboxLabel
              label={
                intl.formatMessage(MESSAGES.attest, {
                  name: userFullName(notaryUser),
                  b,
                }) as ReactElement
              }
              checkbox={
                <Checkbox
                  value="yes"
                  aria-invalid={isAriaInvalid(errors.attested)}
                  {...form.register("attested", { required: true })}
                />
              }
            />
          </div>
        </CheckboxContainer>

        <FormRow>
          <label className={Styles.label} htmlFor={relationshipId}>
            {intl.formatMessage(MESSAGES.relationshipLabel)}
          </label>
          <TextInput
            aria-invalid={isAriaInvalid(errors.relationship)}
            id={relationshipId}
            {...form.register("relationship", { required: defaultRequiredMessage(intl) })}
          />
          <FormattedFieldError inputName="relationship" error={errors.relationship} />
        </FormRow>

        <div>
          <FieldErrorMessage
            inputName="agreements"
            message={errors.attested ? intl.formatMessage(MESSAGES.requiredAttest) : undefined}
          />
        </div>
      </Fields>

      <Actions onBack={onBack} isLoading={loading}>
        {intl.formatMessage(MESSAGES.saveButton)}
      </Actions>
    </Form>
  );
}

const MemoizedAttestWitness = memo(AttestWitness);

export function AttestWitnessForRemoteModal({
  onClose,
  witnessInformation,
  meetingId,
  notaryUser,
}: WithModalProps) {
  const intl = useIntl();
  const handleAttestSubmit = useConfirmWitnessConfig(witnessInformation, meetingId, onClose);
  return (
    <Portal>
      <Modal
        className={Styles.body}
        closeRoute={onClose}
        title={intl.formatMessage(MESSAGES.remoteAttestTitle)}
        autoFocus
      >
        <MemoizedAttestWitness
          witnessInformation={witnessInformation}
          notaryUser={notaryUser}
          onSubmit={handleAttestSubmit}
        />
      </Modal>
    </Portal>
  );
}

export default MemoizedAttestWitness;
